/**
 * SprayPage — Historial de aplicaciones fitosanitarias por lote
 *
 * Muestra las actividades de tipo FITOSANITARIO con sus datos de aplicación:
 *   - Producto / dosis
 *   - Equipo (tractor, dron, mochila)
 *   - Cargas, velocidad, litros de agua
 *   - Hora entrada / salida del lote
 *   - Operador aplicador
 */
import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import {
  Text, Spinner, Badge, Dropdown, Option, Button, Field, Input,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchLoteActivities } from '../api/lotes.api';
import { fetchModules } from '../api/modules.api';
import { parseLoteZoneId } from '../utils/lote-zone-options';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { useRanchoStore } from '../store/rancho.store';
import { CycleCombobox } from '../components/shared/CycleCombobox';
import { DateInput } from '../components/shared/DateInput';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { createSpray, type CreateSprayInput } from '../api/spray.api';
import { fetchProducts } from '../api/inventory.api';
import { KpiCard } from '../components/shared/KpiCard';
import { useAuthStore } from '../store/auth.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { useT } from '../i18n';
import { useToast } from '../hooks/useToast';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { NumberInput } from '../components/shared/NumberInput';
import { useIntlFormat } from '../hooks/useIntlFormat';
import type { Lote, Activity } from '../types/api.types';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: '24px' },
  toolbar: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  kpiRow:  { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '12px',
  },
  tag: {
    display:      'inline-block',
    fontSize:     '11px',
    color:        tokens.colorNeutralForeground3,
    marginTop:    '2px',
  },
});

function NewSprayDialog({ open, onClose, loteId, moduleId }: { open: boolean; onClose: () => void; loteId: string; moduleId?: string }) {
  const t  = useT();
  const toast = useToast();
  const sp = t.spray;
  const qc = useQueryClient();

  const { data: products = [] } = useQuery({ queryKey: ['products'], queryFn: () => fetchProducts() });
  const productOptions = products.map(p => ({ id: p.productId, label: p.name }));

  const [occurredAt, setOccurredAt] = useState('');
  const [productName, setProductName] = useState('');
  const [qty, setQty] = useState('');
  const [unit, setUnit] = useState('litros');
  const [productId, setProductId] = useState('');
  const [crop, setCrop] = useState('');
  const [market, setMarket] = useState<'US' | 'MX' | 'CA'>('MX');
  const [plannedHarvestDate, setPlannedHarvestDate] = useState('');
  const [batchId, setBatchId] = useState('');
  const [equipo, setEquipo] = useState('');
  const [cargas, setCargas] = useState('');
  const [velocidad, setVelocidad] = useState('');
  const [litrosAgua, setLitrosAgua] = useState('');
  const [horaEntrada, setHoraEntrada] = useState('');
  const [horaSalida, setHoraSalida] = useState('');
  const [operador, setOperador] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();
  const [complianceReasons, setComplianceReasons] = useState<string[] | null>(null);

  const reset = () => {
    setOccurredAt(''); setProductName(''); setQty(''); setUnit('litros');
    setProductId(''); setCrop(''); setMarket('MX'); setPlannedHarvestDate(''); setBatchId('');
    setEquipo(''); setCargas(''); setVelocidad(''); setLitrosAgua(''); setHoraEntrada(''); setHoraSalida(''); setOperador('');
    setError(''); fe.clear(); setComplianceReasons(null);
  };

  const hasSprayData = equipo || cargas || velocidad || litrosAgua || horaEntrada || horaSalida || operador;

  const mut = useMutation({
    mutationFn: () => {
      const input: CreateSprayInput = {
        occurredAt, batchId: batchId.trim() || undefined, moduleId,
        materials: [{ productId: productId.trim(), name: productName.trim(), quantity: parseFloat(qty) || 0, unit }],
        productId: productId.trim(), crop: crop.trim(), market,
        plannedHarvestDate: plannedHarvestDate || undefined,
        sprayData: hasSprayData ? {
          equipo: equipo.trim() || undefined,
          cargas: cargas ? parseFloat(cargas) : undefined,
          velocidad: velocidad ? parseFloat(velocidad) : undefined,
          litrosAgua: litrosAgua ? parseFloat(litrosAgua) : undefined,
          horaEntrada: horaEntrada.trim() || undefined,
          horaSalida: horaSalida.trim() || undefined,
          operador: operador.trim() || undefined,
        } : undefined,
      };
      return createSpray(loteId, input);
    },
    onSuccess: (created) => {
      if (created.budgetWarning) toast.warn(t.budgetNotice.title, created.budgetWarning);
      qc.invalidateQueries({ queryKey: ['activities', loteId] });
      reset();
      onClose();
    },
    onError: (e: any) => {
      if (e?.response?.status === 409 && e?.response?.data?.error === 'COMPLIANCE_BLOCKED') {
        setComplianceReasons(e.response.data.reasons ?? []);
      } else {
        setError(e?.response?.data?.error ?? sp.createError);
      }
    },
  });

  const handleSubmit = () => {
    setError(''); setComplianceReasons(null);
    const ok = fe.validate({
      occurredAt: !occurredAt && sp.dateRequired,
      product: (!productName.trim() && sp.productRequired) || (!productId.trim() && sp.productIdRequired),
      crop: !crop.trim() && sp.cropRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '520px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="spray"
              voiceExample={t.telegramHelp.sprayVoice}
              voiceDesc={t.telegramHelp.sprayDesc}
            />
          }>{sp.newApplicationTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px', maxHeight: '65vh', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={sp.fieldOccurredAt} {...fe.props('occurredAt')}>
                  <Input type="datetime-local" value={occurredAt} onChange={(_, d) => { setOccurredAt(d.value); fe.clear('occurredAt'); }} disabled={mut.isPending} />
                </Field>
                <Field label={sp.fieldBatchId}>
                  <CycleCombobox loteId={loteId} moduleId={moduleId} value={batchId} onChange={setBatchId} disabled={mut.isPending} />
                </Field>
              </div>

              <SearchableSelect
                label={sp.fieldProductName}
                placeholder={sp.searchProductPlaceholder}
                noResultsText={t.common.searchNoResults}
                options={productOptions}
                value={productId}
                onChange={(id) => {
                  setProductId(id);
                  fe.clear('product');
                  setProductName(products.find(p => p.productId === id)?.name ?? '');
                }}
                error={fe.errors.product}
                disabled={mut.isPending}
              />

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={sp.fieldQty}>
                  <NumberInput value={qty} onValueChange={(v) => setQty(v)} disabled={mut.isPending} />
                </Field>
                <Field label={sp.fieldUnit}>
                  <Input value={unit} onChange={(_, d) => setUnit(d.value)} disabled={mut.isPending} />
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={sp.fieldCrop} {...fe.props('crop')}>
                  <Input value={crop} onChange={(_, d) => { setCrop(d.value); fe.clear('crop'); }} disabled={mut.isPending} />
                </Field>
                <Field label={sp.fieldMarket}>
                  <Dropdown
                    selectedOptions={[market]}
                    value={market}
                    onOptionSelect={(_, d) => setMarket(d.optionValue as 'US' | 'MX' | 'CA')}
                    disabled={mut.isPending}
                  >
                    <Option value="MX">MX</Option>
                    <Option value="US">US</Option>
                    <Option value="CA">CA</Option>
                  </Dropdown>
                </Field>
              </div>

              <Field label={sp.fieldPlannedHarvest}>
                <DateInput value={plannedHarvestDate} onValueChange={(v) => setPlannedHarvestDate(v)} disabled={mut.isPending} />
              </Field>

              <Text weight="semibold" size={200}>{sp.sprayDataTitle}</Text>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={sp.fieldEquipo}>
                  <Input value={equipo} onChange={(_, d) => setEquipo(d.value)} disabled={mut.isPending} />
                </Field>
                <Field label={sp.fieldOperador}>
                  <Input value={operador} onChange={(_, d) => setOperador(d.value)} disabled={mut.isPending} />
                </Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={sp.fieldCargas}>
                  <NumberInput value={cargas} onValueChange={(v) => setCargas(v)} disabled={mut.isPending} />
                </Field>
                <Field label={sp.fieldVelocidad}>
                  <NumberInput value={velocidad} onValueChange={(v) => setVelocidad(v)} disabled={mut.isPending} />
                </Field>
              </div>
              <Field label={sp.fieldLitrosAgua}>
                <NumberInput value={litrosAgua} onValueChange={(v) => setLitrosAgua(v)} disabled={mut.isPending} />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={sp.fieldHoraEntrada}>
                  <Input value={horaEntrada} onChange={(_, d) => setHoraEntrada(d.value)} placeholder="14:30" disabled={mut.isPending} />
                </Field>
                <Field label={sp.fieldHoraSalida}>
                  <Input value={horaSalida} onChange={(_, d) => setHoraSalida(d.value)} placeholder="16:00" disabled={mut.isPending} />
                </Field>
              </div>

              {complianceReasons && (
                <div style={{ backgroundColor: tokens.colorStatusDangerBackground1, borderRadius: tokens.borderRadiusMedium, padding: '10px' }}>
                  <Text weight="semibold" style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>
                    🚫 {sp.complianceBlockedTitle}
                  </Text>
                  {complianceReasons.map((r, i) => (
                    <Text key={i} block style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px', marginTop: '4px' }}>• {r}</Text>
                  ))}
                </div>
              )}
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              disabled={mut.isPending}
              icon={mut.isPending ? <Spinner size="tiny" /> : undefined}
            >
              {sp.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export function SprayPage() {
  const { formatDate } = useLocaleSettings();
  const s = useStyles();
  const t = useT();
  const { fmtNum } = useIntlFormat();
  const { user } = useAuthStore();
  // Roles con permiso crop:write equivalente (ver OperationsModule.allowedRoles)
  const canWrite = user?.roles.some(r => ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN', 'SUPER_ADMIN'].includes(r)) ?? false;
  const [zoneSelection, setZoneSelection] = useState<string>('');
  const [newAppOpen, setNewAppOpen] = useState(false);

  const activeRanchoId = useRanchoStore((s) => s.activeRanchoId);
  const { data: modules = [] } = useQuery({ queryKey: ['modules-rancho', activeRanchoId], queryFn: () => fetchModules({ ranchoId: activeRanchoId }) });
  const selection = parseLoteZoneId(zoneSelection, modules);

  // useRanchoLotes limpia la selección si el lote ya no está en el rancho activo.
  const { lotes } = useRanchoLotes(true, { value: selection?.loteId ?? '', reset: () => setZoneSelection('') }, undefined, true);
  const zoneOptions = useLoteZoneOptions(lotes as Lote[], modules);
  const activeLoteId = selection?.loteId ?? ((lotes as Lote[])[0]?.loteId ?? '');
  const activeModuleId = selection?.moduleId;
  const activeLote   = (lotes as Lote[]).find(l => l.loteId === activeLoteId);

  const { data: allActivities = [], isLoading } = useQuery<Activity[]>({
    queryKey: ['activities', activeLoteId, activeModuleId ?? null],
    queryFn:  () => fetchLoteActivities(activeLoteId, activeModuleId ? { moduleId: activeModuleId } : undefined),
    enabled:  !!activeLoteId,
  });

  // Filtrar solo aplicaciones fitosanitarias
  const sprays = (allActivities as Activity[]).filter(a => a.type === 'FITOSANITARIO');

  // KPIs simples
  const lastSpray = sprays.length > 0
    ? sprays.reduce((a, b) => a.occurredAt > b.occurredAt ? a : b)
    : null;

  return (
    <div className={s.root}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageTitle>{t.spray.title}</PageTitle>
        {canWrite && activeLoteId && (
          <Button appearance="primary" onClick={() => setNewAppOpen(true)}>
            {t.spray.newApplicationBtn}
          </Button>
        )}
      </div>

      {/* Selector de lote */}
      <div className={s.toolbar}>
        <SearchableSelect
          label={t.spray.zoneFilter}
          placeholder={t.spray.selectLot}
          options={zoneOptions}
          value={zoneSelection || (activeLoteId ? `L:${activeLoteId}` : '')}
          onChange={setZoneSelection}
          noResultsText={t.common.searchNoResults}
        />
      </div>

      {/* KPIs */}
      <div className={s.kpiRow}>
        <KpiCard
          label={t.spray.kpiTotal}
          value={String(sprays.length)}
          sub={activeLote?.cultivo ?? ''}
        />
        {lastSpray && (
          <KpiCard
            label={t.spray.kpiLastApp}
            value={formatDate((lastSpray.occurredAt ?? '').slice(0, 10))}
            sub={lastSpray.materials[0]?.name ?? ''}
          />
        )}
      </div>

      {/* Tabla */}
      <div className={s.card}>
        <Text weight="semibold">{t.spray.tableTitle}</Text>
        {isLoading ? (
          <Spinner size="small" />
        ) : sprays.length === 0 ? (
          <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t.spray.noRecords}</Text>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: '900px' }}>
              <DataTable<Activity>
                rows={[...sprays].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))}
                rowKey={(act) => act.activityId}
                columns={[
                  { key: 'date', header: t.spray.tableDate, width: 1.3, render: (act) => (act.occurredAt ?? '').slice(0, 16).replace('T', ' ') },
                  { key: 'product', header: t.spray.tableProduct, width: 1.6, render: (act) => (
                    <>
                      <div>{act.materials[0]?.name ?? '—'}</div>
                      {act.materials.slice(1).map((m, i) => <div key={i} className={s.tag}>+ {m.name}</div>)}
                    </>
                  ) },
                  { key: 'dosis', header: t.spray.tableDosis, align: 'right', render: (act) => act.materials[0] ? `${act.materials[0].quantity} ${act.materials[0].unit}` : '—' },
                  { key: 'equipo', header: t.spray.tableEquipo, render: (act) => act.sprayData?.equipo
                    ? <Badge appearance="tint" color="informative" size="small">{act.sprayData.equipo}</Badge> : '—' },
                  { key: 'operator', header: t.spray.tableOperator, render: (act) => act.sprayData?.operador ?? act.recordedByName ?? act.recordedBy },
                  { key: 'cargas', header: t.spray.tableCargas, align: 'right', render: (act) => act.sprayData?.cargas ?? '—' },
                  { key: 'velocidad', header: t.spray.tableVelocidad, align: 'right', render: (act) => act.sprayData?.velocidad != null ? `${act.sprayData.velocidad} km/h` : '—' },
                  { key: 'litros', header: t.spray.tableLitrosAgua, align: 'right', render: (act) => act.sprayData?.litrosAgua != null ? `${fmtNum(act.sprayData.litrosAgua)} L` : '—' },
                  { key: 'entrada', header: t.spray.tableHoraEntrada, render: (act) => act.sprayData?.horaEntrada ?? '—' },
                  { key: 'salida', header: t.spray.tableHoraSalida, render: (act) => act.sprayData?.horaSalida ?? '—' },
                ]}
              />
            </div>
          </div>
        )}
      </div>

      <NewSprayDialog open={newAppOpen} onClose={() => setNewAppOpen(false)} loteId={activeLoteId} moduleId={activeModuleId} />
    </div>
  );
}
